// src/hooks/useDiscountedProducts.js
import { useEffect, useState } from "react";
import { applyDiscountsToCart } from "../utils/discountEngine";
import { useCart } from "../context/CartContext";

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

const listPriceOf = (p) => Number(p?.orderConfig?.regular?.price ?? 0);
const typeOf = (p) => p?.subcategory || p?.category || p?.type || null;

/**
 * Display-only discount computation for product listing.
 * - Returns products unchanged, plus a `display` object:
 *   { listUnit, unit, savings, percentOff, discountsApplied }
 * - Does NOT write cart fields like unitPrice/totalPrice/etc.
 */
export default function useDiscountedProducts(rawProducts = []) {
  const { activeDiscounts, user } = useCart();
  const [models, setModels] = useState([]);

  useEffect(() => {
    if (!rawProducts?.length) {
      setModels([]);
      return;
    }

    // Build a minimal "cart-like" array purely for pricing (qty=1 per item)
    const cartLike = rawProducts.map((p) => {
      const listUnit = listPriceOf(p);
      return {
        _id: p._id,
        productId: p._id,
        type: typeOf(p),
        quantity: 1,
        baseUnitPrice: listUnit, // engine baseline
        unitPrice: listUnit, // safety for older engine paths
      };
    });

    const { updatedItems } = applyDiscountsToCart(
      cartLike,
      user,
      activeDiscounts
    );

    const out = rawProducts.map((p, idx) => {
      const listUnit = listPriceOf(p);
      const priced = updatedItems?.[idx];
      const discountedUnit = Number(priced?.unitPrice ?? listUnit);
      const savings = Math.max(0, round2(listUnit - discountedUnit));
      const percentOffRaw =
        listUnit > 0 ? ((listUnit - discountedUnit) / listUnit) * 100 : 0;
      const percentOff = Math.max(0, Math.min(100, Math.round(percentOffRaw)));

      return {
        ...p, // keep product intact
        display: {
          listUnit: round2(listUnit),
          unit: round2(discountedUnit),
          savings,
          percentOff,
          discountsApplied: priced?.discountsApplied || [],
        },
      };
    });

    setModels(out);
  }, [rawProducts, activeDiscounts, user]);

  return models;
}
